import axios from "axios"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"

import Button from "@material-ui/core/Button"
import Dialog from "@material-ui/core/Dialog"
import DialogActions from "@material-ui/core/DialogActions"
import DialogContent from "@material-ui/core/DialogContent"
import DialogContentText from "@material-ui/core/DialogContentText"

import { DialogTitle } from "components/partials"
import { Alert, DangerButton } from "components/widgets"
import { AxiosHelper } from "common/helpers"

const AdminDeleteDialog = props => {
    const { selected, DialogProps } = props
    const { t } = useTranslation()
    const [isLoading, setIsLoading] = useState(false)
    const [isOpen, setIsOpen] = useState(false)
    const [error, setError] = useState(null)

    const contents = {
        one: {
            title: t("view.admin.deleteRowTitle"),
            body: () => t("view.admin.deleteRowDescription"),
            cancel: t("view.admin.keepRow"),
            confirm: t("view.admin.deleteRow")
        },
        many: {
            title: t("view.admin.deleteRowTitle"),
            body: total => t("view.admin.deleteMultipleRowDescription", { total }),
            cancel: t("view.admin.keepRow"),
            confirm: t("view.admin.deleteRow")
        }
    }

    useEffect(() => {
        setIsOpen(selected > 0)
    }, [selected])

    function onClose() {
        setIsOpen(false)
    }

    async function onDelete() {
        setIsLoading(true)

        let hasError = false
        if (props.deleteCallback) {
            try {
                await props.deleteCallback()
            } catch (error) {
                hasError = true

                if (axios.isAxiosError(error)) {
                    if (error.response?.status === 403) {
                        setError(t("errors.insufficientPermission"))
                    } else {
                        const parsed = AxiosHelper.parseError(error)
                        if (typeof parsed === "string") {
                            setError(parsed)
                        } else {
                            setError(t("errors.somethingWentWrong"))
                        }
                    }
                }
            }
        }

        setIsLoading(false)

        if (!hasError) {
            onClose()
        }
    }

    function onExited(node) {
        setError(null)

        if (DialogProps.TransitionProps?.onExited) {
            DialogProps.TransitionProps.onExited(node)
        }
    }

    const content = contents[selected > 1 ? "many" : "one"]

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            {...DialogProps}
            TransitionProps={{
                ...DialogProps.TransitionProps,
                onExited
            }}
        >
            <DialogTitle onClose={onClose}>{content.title}</DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" marginBottom={2}>
                        {error}
                    </Alert>
                )}

                <DialogContentText>{content.body(selected)}</DialogContentText>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{content.cancel}</Button>
                <DangerButton
                    disabled={isLoading}
                    onClick={onDelete}
                    variant="contained"
                >
                    {isLoading ? t("view.admin.deleteRowLoading") : content.confirm}
                </DangerButton>
            </DialogActions>
        </Dialog>
    )
}

export default AdminDeleteDialog
